// /en/finance/documents — Wave 75: EN hub of financial & banking document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/finance/documents";
const URL = `${SITE}${PATH}`;
const TH_URL = `${SITE}/finance/documents`;
const TITLE = "Thai Financial & Bank Documents — 6 Cases | NYC";
const DESC =
  "Thai financial paperwork in six cases: bank statements, balance certificates, salary and income proof, sponsorship letters, loan and mortgage documents, and cross-border source-of-funds evidence.";

export const Route = createFileRoute("/en/finance/documents/")({
  loader: async () => {
    const { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_EN, FINANCE_AUTHORITY_EN } = await import("@/data/finance-doc-groups");
    return {
      __d: { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_EN, FINANCE_AUTHORITY_EN },
      groups: FINANCE_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: TH_URL },
      { rel: "alternate", hrefLang: "x-default", href: TH_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Thai financial and bank document cases",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: FinanceDocumentsEnPage,
});

function FinanceDocumentsEnPage() {
  const { __d: { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_EN, FINANCE_AUTHORITY_EN } } = Route.useLoaderData() as { __d: typeof import("@/data/finance-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">Financial documents / all cases</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Thai financial and bank documents — 6 cases</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Financial paperwork splits by who issued it — a bank, an employer or the applicant themselves — because the
        issuer determines whether a signature must be certified and which legalisation chain applies before the document
        can be used abroad. Each case below lists the documents to prepare, the step order, a pre-filing checklist,
        practical tips and the common pitfalls.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{FINANCE_AUTHORITY_EN}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{FINANCE_APOSTILLE_EN}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {FINANCE_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/en/finance/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {FINANCE_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/finance/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full guide: {g.titleEn} →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Send scans via LINE @nycli, call +66-83-249-4999 or email nycli@ilc.ltd. Our team will confirm the steps, any
        missing documents and a realistic timeline for your case.
      </p>
          <ServiceAuthorityBlock topic="finance" locale="en" />
    </main>
  );
}
